import SummaryFrame from "~/features/configurator/common/components/ConfigurationSummaryComponent/SummaryFrame/SummaryFrame";
import EditButton from "~/features/configurator/common/components/ConfigurationSummaryComponent/EditButton/EditButton";
import { useSelector } from "react-redux";
import { type RootState } from "~/app/store";
import { getHighestVisitedStep, getKmats } from "~/features/configurator/Linear/redux/selectors";
import { type SubstepTile } from "~/features/configurator/Linear/models";
import DataDrivenTable from "~/common/components/Table/DataDrivenTable";
import {
  CSTIC_AUSWAHL_SCHMIERANSCHLUSS_NAME,
  CSTIC_CARRIAGE_LEFT_NAME,
  CSTIC_CARRIAGE_MIDDLE_NAME,
  CSTIC_CARRIAGE_RIGHT_NAME,
  CSTIC_CARRIAGES_SMDS,
  CSTIC_SCHMIERANSCHLUSS_DEGEEE_NAME,
  CSTIC_SCHMIERANSCHLUSS_NAME,
  CSTIC_SCHMIERANSCHLUSS_POSITION_NAME,
  CSTIC_WAGENBAUFORM_NAME,
} from "~/features/configurator/Linear/constants";
import {
  isSchmierAuswahlCsticVisible,
  isSchmierDegreeCsticVisible,
  isSchmierPositionCsticVisible,
} from "~/features/configurator/Carriage/carriageConfiguratorUtils";
import { CARRIAGE_LOCATING_CSTIC_NAME } from "~/features/configurator/Carriage/carriageConfiguratorConstants";
import {
  generateSimpleSubstepData,
  prepareTableView,
} from "~/features/configurator/Linear/v2/configuration/classic/summary/LinearSummaryData";
import { useTranslation, type WithTranslation } from "react-i18next";
import { isTileStandard } from "~/features/configurator/common/tileUtils";
import { getConfiguratorDefinition } from "~/features/configurator/configuratorRegistry";
import { useXmlConfiguratorTranslations } from "~/features/configurator/common/useXmlConfiguratorTranslations";
import { SubstepType } from "~/features/configurator/Linear/dtos";
import { CARRIAGE_CONFIGURATOR_PRODUCT_CODE } from "~/domains/configurator/constants";
import { getMainKmat } from "~/features/configurator/Shaft/common/commonShaftHelpers";

const TWO_COL_TABLE_FULL_PAGE = ["30%", "70%"];
const TWO_COL_TABLE_REVIEW = ["50%", "50%"];

const getSteps = (state: RootState) => state.linearConfigurator.steps;
const getSubsteps = (state: RootState) => state.linearConfigurator.substeps;

interface Props {
  isFullReview: boolean;
  onStepChange?: () => void;
}

interface StepData {
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value?: any;
  isStandard: boolean;
}

const selectStepName = (index: number) => (state: RootState) => {
  return state.linearConfigurator.steps[index - 1]?.name ?? "";
};

export const CarriageSummaryData = ({ isFullReview, onStepChange }: Props) => {
  const { t } = useTranslation();
  const xmlT = useXmlConfiguratorTranslations().t!;
  const dataTableHeader = [t("linearConfigSummary.step.header")];
  const twoColTable = isFullReview ? TWO_COL_TABLE_FULL_PAGE : TWO_COL_TABLE_REVIEW;

  const step1Data = useSelector(prepareStep1Data);
  const step2DataTable = useSelector(prepareStep2Data);
  const step3DataTable = useSelector(prepareStep3Data(t));

  const step1Name = xmlT(useSelector(selectStepName(1)));
  const step2Name = xmlT(useSelector(selectStepName(2)));
  const step3Name = xmlT(useSelector(selectStepName(3)));

  return (
    <>
      {step1Data && (
        <SummaryFrame num={1} containerClassName={"d-flex flex-direction-column"} name={step1Name}>
          <div>
            <h3 className="pb-0">
              <div>{step1Data.baseDesignation}</div>
            </h3>
            <EditButton index={0} onStepChange={onStepChange} />
          </div>
        </SummaryFrame>
      )}
      {step2DataTable && (
        <SummaryFrame num={2} name={step2Name}>
          <DataDrivenTable header={dataTableHeader} data={step2DataTable} columnFlex={twoColTable} />
          <EditButton index={1} onStepChange={onStepChange} />
        </SummaryFrame>
      )}
      {step3DataTable && (
        <SummaryFrame num={3} name={step3Name}>
          <DataDrivenTable header={dataTableHeader} data={step3DataTable} columnFlex={twoColTable} />
          <EditButton index={2} onStepChange={onStepChange} />
        </SummaryFrame>
      )}
    </>
  );
};

export const prepareStep1Data = (state: RootState) => {
  return {
    baseDesignation: getConfiguratorDefinition(CARRIAGE_CONFIGURATOR_PRODUCT_CODE).generateDesignation!(
      state.linearConfigurator.kmats,
      state.linearConfigurator,
    ),
  };
};

const prepareStep2Data = (state: RootState) => {
  const highestVisitedStep = getHighestVisitedStep(state);
  if (highestVisitedStep < 1) {
    // 2 - 1, because 0 indexed
    return null;
  }

  const substeps = getSubsteps(state);
  const kmats = getKmats(state);

  const step3data = [];

  const systemCoatingData = generateSimpleSubstepData(substeps.Coating, kmats);
  if (systemCoatingData) {
    step3data.push(systemCoatingData);
  }

  const preloadData = generateSimpleSubstepData(substeps.Preload, kmats);
  if (preloadData) {
    step3data.push(preloadData);
  }

  const accuracyData = generateSimpleSubstepData(substeps.Accuracy, kmats);
  if (accuracyData) {
    step3data.push(accuracyData);
  }

  const heightSorting = generateSimpleSubstepData(substeps.CarriageType, kmats);
  if (heightSorting && substeps.CarriageType.isVisible) {
    step3data.push(heightSorting);
  }

  if (step3data.length > 0) {
    return prepareTableView(step3data);
  } else {
    return null;
  }
};

export const prepareStep3Data = (t: WithTranslation["t"]) => (state: RootState) => {
  const highestVisitedStep = getHighestVisitedStep(state);
  if (highestVisitedStep < 2) {
    // 2 - 1, because 0 indexed
    return null;
  }

  const substeps = getSubsteps(state);
  const kmats = getKmats(state);

  const carriageData: StepData[] = [];
  const carriageKmat = getMainKmat({ kmats });

  const lubricationSubstep = substeps.Lubrication;
  if (lubricationSubstep && lubricationSubstep.selectedTile !== null) {
    const allTiles: SubstepTile[] = [...lubricationSubstep.tiles];
    lubricationSubstep.groupedTiles.forEach((group) => allTiles.push(...group.tiles));

    const selectedTile = allTiles[lubricationSubstep.selectedTile];
    carriageData.push({
      name: substeps.Lubrication.name,
      value: selectedTile.name,
      isStandard: isTileStandard(selectedTile, kmats),
    });
  }

  const smdsCstic = carriageKmat.cstics[CSTIC_CARRIAGES_SMDS];

  if (substeps.SMDS.isVisible && smdsCstic.value != null) {
    carriageData.push({
      name: substeps.SMDS.name,
      value: substeps.SMDS.tiles[substeps.SMDS.selectedTile!].name,
      isStandard: smdsCstic.value == "N",
    });
  }
  const smdeCstic = carriageKmat.cstics["VADK_065"];

  if (substeps.SMDE.isVisible && substeps.SMDE.selectedTile !== null) {
    carriageData.push({
      name: substeps.SMDE.name,
      value: substeps.SMDE.tiles[substeps.SMDE.selectedTile!].name,
      isStandard: smdeCstic.value?.includes("N") ?? false,
    });
  }

  const locatingFaceSubstep = substeps.LocatingFace;
  const locatingFaceCstic = carriageKmat.cstics[CARRIAGE_LOCATING_CSTIC_NAME];
  if (locatingFaceCstic && locatingFaceSubstep) {
    const val = locatingFaceCstic.values.find((val) => val.value === locatingFaceCstic.value);
    carriageData.push({
      name: locatingFaceSubstep.name,
      value: val?.label ?? locatingFaceCstic.value,
      isStandard: val?.standard ?? false,
    });
  }

  const carriageTypeSubstep = substeps[SubstepType.CarriageType];
  const carriageTypeSelectedTile = carriageTypeSubstep.tiles[carriageTypeSubstep.selectedTile!];
  const singleCarriageReference = carriageKmat.cstics[CSTIC_WAGENBAUFORM_NAME];
  const carriageRefSelectedTile = singleCarriageReference.values.find(
    (singleValue) => singleValue.value === singleCarriageReference.value,
  );
  if (singleCarriageReference.value !== null && singleCarriageReference.value !== undefined) {
    carriageData.push({
      name: carriageTypeSubstep.name,
      value: carriageTypeSelectedTile?.name,
      isStandard: carriageRefSelectedTile?.standard || false,
    });
  }

  const kitLeft = carriageKmat.cstics[CSTIC_CARRIAGE_LEFT_NAME];
  if (kitLeft && kitLeft.value !== null && kitLeft.value !== undefined) {
    carriageData.push({
      name: kitLeft.label,
      value: kitLeft.value,
      isStandard: false,
    });
  }

  const kitMid = carriageKmat.cstics[CSTIC_CARRIAGE_MIDDLE_NAME];
  if (kitMid && kitMid.value !== null && kitMid.value !== undefined) {
    carriageData.push({
      name: kitMid.label,
      value: kitMid.value,
      isStandard: false,
    });
  }

  const kitRight = carriageKmat.cstics[CSTIC_CARRIAGE_RIGHT_NAME];
  if (kitRight && kitRight.value !== null && kitRight.value !== undefined) {
    carriageData.push({
      name: kitRight.label,
      value: kitRight.value,
      isStandard: false,
    });
  }

  const lubrication = carriageKmat.cstics[CSTIC_SCHMIERANSCHLUSS_NAME];
  if (lubrication) {
    const lubricationSelectedTile = lubrication.values.find((singleValue) => singleValue.key === lubrication.value);
    if (lubrication.value !== null) {
      carriageData.push({
        name: lubrication.label,
        value: lubricationSelectedTile?.label ?? "-",
        isStandard: lubricationSelectedTile!.standard!,
      });
    }
  }

  const lubricationPosition = carriageKmat.cstics[CSTIC_SCHMIERANSCHLUSS_POSITION_NAME];
  if (lubricationPosition && isSchmierPositionCsticVisible(carriageKmat.cstics)) {
    const lubricationPositionSelectedTile = lubricationPosition.values.find(
      (singleValue) => singleValue.key === lubricationPosition.value,
    );
    carriageData.push({
      name: lubricationPosition.label,
      value: lubricationPositionSelectedTile?.label ?? "-",
      isStandard: lubricationPositionSelectedTile?.standard ?? false,
    });
  }

  const lubricationPortSelection = carriageKmat.cstics[CSTIC_AUSWAHL_SCHMIERANSCHLUSS_NAME];
  if (lubricationPortSelection && isSchmierAuswahlCsticVisible(carriageKmat.cstics)) {
    const lubricationPortSelectionSelectedTile = lubricationPortSelection.values.find(
      (singleValue) => singleValue.key === lubricationPortSelection.value,
    );
    carriageData.push({
      name: lubricationPortSelection.label,
      value: lubricationPortSelectionSelectedTile?.label ?? "-",
      isStandard: lubricationPortSelectionSelectedTile?.standard ?? false,
    });
  }

  const lubricationDegree = carriageKmat.cstics[CSTIC_SCHMIERANSCHLUSS_DEGEEE_NAME];
  if (lubricationDegree && isSchmierDegreeCsticVisible(carriageKmat.cstics)) {
    const lubricationDegreeSelectedTile = lubricationDegree.values.find(
      (singleValue) => singleValue.key === lubricationDegree.value,
    );
    carriageData.push({
      name: lubricationDegree.label,
      value: lubricationDegreeSelectedTile?.label ?? "-",
      isStandard: lubricationDegreeSelectedTile?.standard ?? false,
    });
  }

  return prepareTableView(carriageData);
};
